//import THREE from "three";
import RenderManager from "../rendering/render-manager";
import Network from "../miniviews/network";
import Spline from "./spline";
import CoilEmField from "../coilEmField/coilEmField";
import MeanMedianMode from "../meanMedianMode/meanMedianMode";
import Atom from "../chemistry/atom";
import TerrainCar from "../terrainCar/terrainCar";
import Molecule from "../chemistry/molecule";
import FerrisWheel from "../objects/ferris-wheel";
import Racetrack from "../objects/racetrack";
import NexusMesh from "../objects/nexus-mesh";
import { HotspotMesh } from "./sprites/IconTexture";
import Star from "../objects/star/star";
import Water from "../objects/water";
import ensureArray from '../utils/ensure-array';
import { addTeachingCylinder } from "../geometry-teaching/interactable-geometry";
import ScalpelPlane from "../objects/scalpel-plane";
import ProceduralObject from "../objects/procedural/procedural-object";
import getVWKey from "../helpers/getVWKey";

const tmpObjectMap = {};
const CAPTURE_LOAD_EXCEPTION = false;

/**
 * Loads an object.
 * @param {Object} props Parameter.
 * @param {String} props.uuid Object UUID.
 * @param {Object} props.data Object data.
 * @param {Object<string, THREE.BufferGeometry>} props.geometries Available geometries.
 * @param {Object<string, THREE.Material>} props.materials Available materials.
 * @param {boolean} props.visible Is object visible.
 * @return {Promise}
 */
async function loadObject({ uuid, data, geometries, materials, visible, assetData }) {
    async function loadObject() {
        let object = null;
        let isTrackable = false;

        switch (data.type) {
            case "Object3D": object = await createObject3d(); break;
            case "Gate": object = await createGate(data); break;
            case "Mesh":
            case "SkinnedMesh":
                object = await createMesh(data, geometries, materials);
                isTrackable = true;
                break;
            case "NexusMesh":
                object = await createNexusMesh(data, materials);
                break;
            case "Sprite":
                object = await createSprite(data, materials);
                isTrackable = true;
                break;
            case "Bone": object = await createBone(data); break;
            case "Atom": object = await createAtom(data); break;
            case "TerrainCar": object = await createTerrainCar(data); break;
            case "Molecule":
                object = await createMolecule(data);
                if (object.error) {
                    assetData[uuid].meshes.forEach(uuid => {
                        delete assetData[uuid];
                    })
                    delete assetData[uuid];
                    object = null;
                }
                break;
            case "Star": object = await createStar(data); break;
            case "Water":
                object = await createWater(data);
                isTrackable = true;
                break;
            case "CoilEmField": object = await createCoilEmField(data); break;
            case "MeanMedianMode": object = await createMeanMedianMode(data); break;
            case "InteractableGeometry": object = await createInteractiveGeometry(data); break;
            case "Group": object = await createGroup(data); break;
            case "Ferris": object = await createFerrisWheel(data); break;
            case "Racetrack": object = await createRacetrack(data); break;
            case "Spline":
                object = await createSpline(data);
                isTrackable = true;
                break;
            case "ScalpelPlane": object = await createScalpelPlane(data); break;
            case "ProceduralMesh":
            case "MoleculeMesh":
                if (assetData[data.parent].meshes.indexOf(uuid) !== -1) {
                    object = await createTempObject(uuid);
                    isTrackable = true;
                } else {
                    delete assetData[uuid];
                }
                break;
            case "ProceduralObject":
                object = await createProceduralObject(data, assetData);
                break;
            default:
                object = await createGroup(data);
                console.warn("Unknown object of type:", data.type);
                break;
        }

        if (!object) return { uuid, object: null, error: true };

        object.name = data.name;
        object.refId = data.refId;

        delete data.refId;

        object.uuid = uuid;
        object.visible = visible;
        object.renderOrder = data.renderOrder || 0;

        if ('originalName' in data) {
            object.originalName = data.originalName;
        }

        Q3.TransformModifiers.applyFlagsFromData(data, object);

        Object.keys(data).forEach(k => {
            if (k === "sizeSm") return;

            if (["material", "geometry", "type", "parent"].indexOf(k) === -1) {
                if (["position", "rotation", "scale", "up"].indexOf(k) > -1) {
                    const vpType = getVWKey();
                    const saveObject = vpType === "sizeSm" && data[vpType]
                        ? data[vpType]
                        : data;

                    Object.assign(object[k], saveObject[k]);
                } else {
                    if (object.hasOwnProperty(k)) object[k] = data[k];
                    else object.userData[k] = data[k];
                }
            }
        });

        if (object.isSpline) object.updateSpline();
        if (isTrackable) RenderManager.raycaster.makeTrackable(object);

        return { uuid, object };
    }

    if (CAPTURE_LOAD_EXCEPTION) {
        try { return await loadObject(); }
        catch (e) {
            console.error(e);
            return { uuid, object: null, error: true, reason: e };
        }
    } else return await loadObject();
}

/**
 * Finalizes object creation by applying required properties.
 * @param {Object} props Parameter.
 * @param {THREE.Object3D} props.object Object to which to apply properties.
 * @param {THREE.Object3D[]} props.objects Object list.
 * @param {Object[]} props.slides Slides.
 * @param {string} props.uuid Object UUID.
 * @param {Object} props.data Object data.
 * @param {THREE.Material[]} props.materials Material list.
 * @param {number} props.activeSlide Active slide.
 */
function finalizeObject({ object, objects, slides, uuid, activeSlide, data, materials }) {
    // Color change on slide 0 on load
    const empty = {};
    const slideAnimations = slides[activeSlide] ? slides[activeSlide].animation : null;
    const slideMatAnimations = (slides[activeSlide] ? slides[activeSlide].materialAnimation : null) || empty;
    const anim = slideAnimations ? slideAnimations[uuid] : null;
    const matUuids = ensureArray(data.material);
    const matAnims = matUuids.map(matUuid => Object.assign({}, anim || empty, slideMatAnimations[matUuid] || empty));

    object.children = object.children.filter(x => x);

    if (anim) {
        const vpType = getVWKey();
        const placement = vpType === "sizeSm" && anim[vpType] ? anim[vpType] : anim;

        for (let param of ['position', 'rotation', 'scale']) {
            Object.assign(object[param], placement[param]);
        }
    }

    for (let [i, material] of ensureArray(object.material).entries()) {
        let matAnim = matAnims[i];

        if (material && matAnim && matAnim.color) material.color.setHex(matAnim.color);
        if (material && matAnim && matAnim.opacity >= 0) {
            if (matAnim.opacity !== null) {
                material.opacity = matAnim.opacity;
            }
            material.transparent = matAnim.transparent || false;
        }
    }

    const asset = data[uuid];

    // Parenting
    const parentUuid = asset.parent;
    let scene = RenderManager.activeScene;

    if (!parentUuid && asset.parent_world) {
        const parentWorld = Network.getWorld(asset.parent_world);
        if (parentWorld) scene = parentWorld.gate.scene;
    }

    const parentObject = parentUuid ? 
        (objects.filter(x => x).find(o => o.uuid === parentUuid)?.object ? 
            objects.filter(x => x).find(o => o.uuid === parentUuid).object : 
            scene) : 
        scene;

    parentObject.add(object);

    // Movable parent
    Q3.TransformModifiers.applyMovableParentFromData(asset, object, objects);

    //clipping planes setup
    const clipId = asset.clipMaterial;
    if (clipId) {
        const normalMat = new THREE.Matrix4();
        object.userData.plane = new THREE.Plane();
        object.onBeforeRender = () => {
            const normal = new THREE.Vector3(
                object.geometry.attributes.normal.array[0],
                object.geometry.attributes.normal.array[1],
                object.geometry.attributes.normal.array[2]
            );
            const point = new THREE.Vector3(
                object.geometry.attributes.position.array[0],
                object.geometry.attributes.position.array[1],
                object.geometry.attributes.position.array[2]
            );
            normalMat.identity().extractRotation(object.matrixWorld);
            normal.applyMatrix4(normalMat);
            point.applyMatrix4(object.matrixWorld);
            object.userData.plane.setFromNormalAndCoplanarPoint(normal, point);
        };

        materials[clipId].clippingPlanes = materials[clipId].clippingPlanes || [];
        materials[clipId].clippingPlanes.push(object.userData.plane);
        materials[clipId].clipShadows = true;
        materials[clipId].needsUpdate = true;
    }
}

/**
 * Binds the skeleton to mesh.
 * @param {Object} props Parameters.
 * @param {String} props.uuid Object UUID.
 * @param {Object} props.data Project data.
 * @param {THREE.SkinnedMesh} props.object Skinned mesh to which to bind skeleton.
 * @param {THREE.Objects[]} props.objects Object list.
 */
function bindSkeleton({ uuid, data, object, objects }) {
    if (object && object.isSkinnedMesh && data.assets.skeletons && data.assets.skeletons[uuid]) {
        const skeletonData = data.assets.skeletons[uuid];
        const bindMatrices = [];

        for (let i = 0, len = skeletonData.inverses.length / 16; i < len; i++)
            bindMatrices.push(new THREE.Matrix4().fromArray(skeletonData.inverses, i * 16));

        const skeleton = new THREE.Skeleton(
            skeletonData.bones.map(id => objects[id]),
            bindMatrices
        );

        for (let material of ensureArray(object.material)) {
            material.skinning = true;
            material.needsUpdate = true;
        }

        object.frustumCulled = false;

        object.bind(skeleton, new THREE.Matrix4().fromArray(skeletonData.bind));
    }

}

export default loadObject;
export { loadObject, finalizeObject, bindSkeleton };

/**
 * Creates empty object.
 * @return {Promise}
 */
async function createObject3d() { return new THREE.Object3D(); }

/**
 * Creates group object.
 * @return {Promise}
 */
async function createGroup() { return new THREE.Group(); }

/**
 * Creates bone object.
 * @return {Promise}
 */
async function createBone() { return new THREE.Bone(); }

/**
 * Creates gate object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createGate(data) {
    const gate = Network.createGate({ worldUuid: data.world_id, parentUuid: data.parent_world });

    if (!gate) throw new Error("Probably was deleted.");
    return gate;
}

/**
 * Creates sprite object.
 * @param {Object} data Object data.
 * @param {THREE.Material[]} materials Material list.
 * @return {Promise}
 */
async function createSprite(data, materials) {
    return new THREE.Sprite(getMaterialsFromUuids(materials, data.material));
}

/**
 * Creates mesh object.
 * @param {Object} data Object data.
 * @param {THREE.BufferGeometry[]} geometries Geometry list.
 * @param {THREE.Material[]} materials Material list.
 * @return {Promise}
 */
async function createMesh(data, geometries, materials) {
    const constructor = data.type === "Mesh"
        ? data.subType === "IconHotspot"
            ? HotspotMesh
            : THREE.Mesh
        : THREE.SkinnedMesh;
    const mesh = new constructor(geometries[data.geometry], getMaterialsFromUuids(materials, data.material));

    mesh.castShadow = mesh.receiveShadow = true;

    if (data.subType) { mesh.subType = data.subType; }

    return mesh;
}

async function createNexusMesh(data, materials) {
    const mesh = new NexusMesh(data.geometry, getMaterialsFromUuids(materials, data.material));

    mesh.castShadow = mesh.receiveShadow = true;

    return mesh;
}

async function createScalpelPlane() { return new ScalpelPlane(); }

async function createProceduralObject(data, assetData) {
    const object = new ProceduralObject(data.displayType || ProceduralObject.Shape_T.TETRAHEDRON);
    const texts = data.meshes;

    try {
        object.createMeshes();

        if ("opacity" in data) object.setOpacity(data.opacity);

        if ("color" in data) object.geometries.color = data.color;
        if ("textColor" in data) object.geometries.textColor = data.textColor;
        if ("edgeColor" in data) object.geometries.edgeColor = data.edgeColor;

        if ("font" in data) object.geometries.font = data.font;
        if ("fontSize" in data) object.geometries.fontSize = data.fontSize;

        texts.filter(uuid => tmpObjectMap[uuid]).forEach((uuid, index) => {
            tmpObjectMap[uuid].resolve(object.children[index]);
            object.setText(index, assetData[uuid].text);
        });
    } catch (e) {
        texts.forEach(uuid => tmpObjectMap[uuid].reject(e));
        throw e;
    }

    return object;
}

/**
 * Receives a material UUID or an array of material UUIDs and
 * returns a material or an array of materials respectively
 */
function getMaterialsFromUuids(materials, uuids) {
    if (uuids instanceof Array) {
        return uuids.map(uuid => materials[uuid]);
    } else {
        return materials[uuids];
    }
}

/**
 * Creates racetrack object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createRacetrack(data) {
    return new Racetrack({
        track: data.asset,
        trackLength: data.trackLength,
        trackRadius: data.trackRadius,
        actionUuid: data.actionUuid,
        axisOffset: data.axisOffset
    });
}

/**
 * Creates ferris wheel object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createFerrisWheel(data) { return new FerrisWheel(); }

/**
 * Creates atom object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createAtom(data) {
    const counts = data.counts;
    const atom = new Atom(counts.proton, counts.neutron, counts.electron);

    atom.enablePeriodicTable = data.periodicTable ? data.periodicTable.visible : false;
    atom.interactiveTable = data.periodicTable ? data.periodicTable.interactive : false;
    atom.infoBoxVisible = data.infoVisible ? data.infoVisible.box : true;
    atom.infoLabelsVisible = data.infoVisible ? data.infoVisible.labels : true;
    atom.controlsVisible = data.controlsVisible !== undefined ? data.controlsVisible : true;

    return atom;
}

/**
 * Creates interactive car object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createTerrainCar(data) {
    const terrainCar = new TerrainCar(data.interactiveCar);
    data.interactiveCar = JSON.parse(JSON.stringify(terrainCar.params));

    return terrainCar;
}

/**
 * Creates interactive geometry object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createInteractiveGeometry(data) { return addTeachingCylinder(); }

/**
 * Creates molecule object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createMolecule(data) {
    const formula = typeof data.formula === "string" ? data.formula : "";
    const assetUri = "assetUri" in data ? data.assetUri : null;
    const atoms = data.meshes;
    const molecule = new Molecule(formula, data.displayType || "ball-stick", true);

    try {
        const _assetUri = await molecule.createMeshes(assetUri);
        if (!_assetUri) molecule.error = true;
        if (!assetUri) data.assetUri = _assetUri;

        atoms.forEach((uuid, index) => {
            const atom = molecule.children[index];

            if (tmpObjectMap[uuid]) tmpObjectMap[uuid].resolve(atom);
            else {
                const idx = molecule.children.indexOf(atom);
                molecule.children[idx] = null;
            }
        });

    } catch (e) {
        molecule.error = true;
        atoms.forEach(uuid => {
            if (tmpObjectMap[uuid])
                tmpObjectMap[uuid].reject(e);
        });
        throw e;
    }

    return molecule;
}

function createTempObject(uuid) {
    let resolve, reject;

    const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
    }).then(result => {
        delete tmpObjectMap[uuid];
        return result;
    }).catch(exception => {
        delete tmpObjectMap[uuid];
        return exception;
    });

    tmpObjectMap[uuid] = Object.freeze({ resolve, reject });

    return promise;
}

async function createWater(data) {
    return new Water({
        alpha: data.alpha,
        distortionScale: data.distortionScale,
        size: data.size
    });
}

async function createStar(data) {
    const star = new Star();
    star.temperature = data.temperature === undefined ? star.temperature : data.temperature;
    RenderManager.raycaster.makeTrackable(star.core);

    return star;
}

/**
 * Creates EM coil object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createCoilEmField(data) {
    const paramCallback = (field, name, value) => {
        data.coilEmField[name] = value;
    };

    const field = new CoilEmField({ paramCallback });
    const fieldData = data.coilEmField;

    field.coilRadius = fieldData.coilRadius;
    field.coilLength = fieldData.coilLength;
    field.turns = fieldData.turns !== undefined ? fieldData.turns : 40;
    field.current = fieldData.current;
    field.voltage = fieldData.voltage;
    field.controlsEnabled = fieldData.controlsEnabled !== undefined ? fieldData.controlsEnabled : true;

    return field;
}

/**
 * Creates mean-median-mode object.
 * @param {Object} data Object data.
 * @return {Promise}
 */
async function createMeanMedianMode(data) {
    const dragCallback = (mmm, index, value) => {
        data.meanMedianMode.values[index] = value;
    };

    const mmm = new MeanMedianMode({ dragCallback });
    const mmmData = data.meanMedianMode;

    mmm.setGridSize(mmmData.size);
    mmm.setValues(mmmData.values);
    mmm.setModelIds(mmmData.modelIds);

    if ("spacing" in mmmData) mmm.setSpacing(mmmData.spacing);

    mmm.showInfo = mmmData.showInfo;

    if ("showAllHeights" in mmmData) mmm.showAllHeights = mmmData.showAllHeights;
    if ("showCalc" in mmmData) mmm.showCalc = mmmData.showCalc;

    const showParams = mmmData.showParams;

    if (showParams) {
        for (let name in showParams) {
            mmm.showParam(name, showParams[name]);
        }
    }

    return mmm;
}


async function createSpline(data) { return new Spline(); }
